import type { Picture, UploadForm } from "@repo/shared-types";
import { ApiError } from "@repo/api-client";

/**
 * Pictures as the web app sends them (7g): shrunk, and cropped to their
 * shape, in the browser, then posted straight to the store with the form the
 * api signed, and confirmed. The api never resizes: it refuses what is
 * larger than its kind allows, so this is where a 12 MB phone photo becomes a
 * 2048 px JPEG of a megabyte or so.
 *
 * A crop is where the frame sits on the picture: how far it is zoomed in
 * (1 is the largest frame of its shape that fits), and its centre as a
 * fraction of the picture's width and height. The arithmetic is pure, so it
 * is tested here; drawing needs a browser.
 */

export interface Crop {
  zoom: number;
  cx: number;
  cy: number;
}

/** How far a frame may zoom in: past this a picture is mostly blur. */
export const MAX_ZOOM = 4;

/** The middle of the picture, at the largest frame that fits. */
export const CENTRED: Crop = { zoom: 1, cx: 0.5, cy: 0.5 };

/** A shape as the api names it — "1:1", "16:9" — as a width over a height. */
export function ratioOf(aspect: string | null): number | null {
  const match = aspect?.match(/^(\d+):(\d+)$/);
  if (!match) return null;
  return Number(match[1]) / Number(match[2]);
}

export interface Rect {
  sx: number;
  sy: number;
  sw: number;
  sh: number;
}

/** The part of a w×h picture a crop keeps, in its pixels, held inside it. */
export function sourceRect(w: number, h: number, ratio: number, crop: Crop): Rect {
  const { zoom, cx, cy } = clampCrop(w, h, ratio, crop);
  const fullWidth = Math.min(w, h * ratio);
  const sw = fullWidth / zoom;
  const sh = sw / ratio;
  return { sx: cx * w - sw / 2, sy: cy * h - sh / 2, sw, sh };
}

/**
 * A crop held to what the picture can give: zoomed between 1 and MAX_ZOOM,
 * and moved no further than keeps the frame covered.
 */
export function clampCrop(w: number, h: number, ratio: number, crop: Crop): Crop {
  const zoom = Math.min(MAX_ZOOM, Math.max(1, Number.isFinite(crop.zoom) ? crop.zoom : 1));
  const sw = Math.min(w, h * ratio) / zoom;
  const sh = sw / ratio;
  const hold = (centre: number, span: number, size: number) => {
    const half = span / size / 2;
    const at = Number.isFinite(centre) ? centre : 0.5;
    return Math.min(1 - half, Math.max(half, at));
  };
  return { zoom, cx: hold(crop.cx, sw, w), cy: hold(crop.cy, sh, h) };
}

/**
 * The crop after dragging the picture by (dx, dy) screen pixels inside a frame
 * frameWidth pixels wide: the picture follows the finger, so the frame moves
 * the other way across it.
 */
export function dragBy(w: number, h: number, ratio: number, crop: Crop, dx: number, dy: number, frameWidth: number): Crop {
  const { sw } = sourceRect(w, h, ratio, crop);
  const perScreenPixel = sw / frameWidth;
  return clampCrop(w, h, ratio, {
    zoom: crop.zoom,
    cx: crop.cx - (dx * perScreenPixel) / w,
    cy: crop.cy - (dy * perScreenPixel) / h,
  });
}

/** The crop zoomed to a level, around the frame's centre. */
export function zoomTo(w: number, h: number, ratio: number, crop: Crop, zoom: number): Crop {
  return clampCrop(w, h, ratio, { ...crop, zoom });
}

/** A w×h picture shrunk to fit maxSide on its longest side — never enlarged. */
export function fitWithin(w: number, h: number, maxSide: number): { width: number; height: number } {
  const scale = Math.min(1, maxSide / Math.max(w, h));
  return { width: Math.max(1, Math.round(w * scale)), height: Math.max(1, Math.round(h * scale)) };
}

/**
 * The size a cropped picture is drawn at: the crop's own, shrunk to fit — and
 * for a shape, its height worked out from its width, so it is the shape
 * exactly, as the api checks.
 */
export function outputSize(rect: Rect, maxSide: number, ratio: number | null): { width: number; height: number } {
  if (ratio === null) return fitWithin(rect.sw, rect.sh, maxSide);
  const longest = Math.min(maxSide, Math.floor(Math.max(rect.sw, rect.sh)));
  const width = ratio >= 1 ? longest : Math.max(1, Math.round(longest * ratio));
  return { width, height: Math.max(1, Math.round(width / ratio)) };
}

/** A crop as a form sends it, and back. A missing or broken one is the middle. */
export function writeCrop(crop: Crop): string {
  const round = (n: number) => Math.round(n * 10000) / 10000;
  return JSON.stringify({ zoom: round(crop.zoom), cx: round(crop.cx), cy: round(crop.cy) });
}

export function readCrop(raw: FormDataEntryValue | null): Crop {
  if (typeof raw !== "string" || raw === "") return CENTRED;
  try {
    const parsed = JSON.parse(raw) as Partial<Crop>;
    const number = (value: unknown, fallback: number) =>
      typeof value === "number" && Number.isFinite(value) ? value : fallback;
    return { zoom: number(parsed.zoom, 1), cx: number(parsed.cx, 0.5), cy: number(parsed.cy, 0.5) };
  } catch {
    return CENTRED;
  }
}

/** Something about a picture worth telling the person who chose it. */
export class PictureError extends Error {}

const UNREADABLE =
  "This picture can’t be read here. Choose a JPEG or PNG — on a computer, export a HEIC photo as JPEG first.";

/**
 * A picture the right way up: decoded by the browser, which turns a phone's
 * photo by its EXIF orientation as it does. HEIC decodes only where the
 * browser can — Safari can; Chrome and Firefox on a computer cannot.
 */
async function decode(file: Blob): Promise<{ source: CanvasImageSource; width: number; height: number; close: () => void }> {
  if (typeof createImageBitmap === "function") {
    try {
      const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
      return { source: bitmap, width: bitmap.width, height: bitmap.height, close: () => bitmap.close() };
    } catch {
      // Older Safari takes no options; the image element below still works.
    }
  }
  const url = URL.createObjectURL(file);
  try {
    const image = new Image();
    image.src = url;
    await image.decode();
    return { source: image, width: image.naturalWidth, height: image.naturalHeight, close: () => {} };
  } catch {
    URL.revokeObjectURL(url);
    throw new PictureError(UNREADABLE);
  }
}

/**
 * A picture as the store will take it: cropped to the form's shape, shrunk to
 * its longest side, a JPEG under its byte limit. Drawn straight at the size it
 * is sent at, so a phone never holds a full-size canvas.
 */
export async function preparePicture(
  file: Blob,
  form: Pick<UploadForm, "maxSide" | "aspect" | "maxBytes">,
  crop: Crop = CENTRED,
): Promise<Blob> {
  const decoded = await decode(file);
  try {
    const ratio = ratioOf(form.aspect);
    const rect =
      ratio === null
        ? { sx: 0, sy: 0, sw: decoded.width, sh: decoded.height }
        : sourceRect(decoded.width, decoded.height, ratio, crop);
    const { width, height } = outputSize(rect, form.maxSide, ratio);
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d");
    if (!context) throw new PictureError(UNREADABLE);
    context.imageSmoothingQuality = "high";
    // A JPEG keeps no transparency, and a canvas starts transparent black: a
    // logo with a clear background would come out on black. White, instead.
    context.fillStyle = "#ffffff";
    context.fillRect(0, 0, width, height);
    context.drawImage(decoded.source, rect.sx, rect.sy, rect.sw, rect.sh, 0, 0, width, height);
    for (const quality of [0.85, 0.7, 0.55]) {
      const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/jpeg", quality));
      if (blob && blob.size <= form.maxBytes) return blob;
    }
    throw new PictureError("This picture is too large to send, even shrunk. Choose a smaller one.");
  } finally {
    decoded.close();
  }
}

/**
 * Posts a prepared file to the store with the form the api signed: every
 * field, then the file, last, as the store requires. Never with the app's
 * cookies — the store is another origin, and owes them nothing.
 */
export async function sendToStore(form: UploadForm, file: Blob, send: typeof fetch = fetch): Promise<void> {
  const body = new FormData();
  for (const [name, value] of Object.entries(form.fields)) body.append(name, value);
  body.append("file", file, "picture.jpg");
  const response = await send(form.url, { method: "POST", body, credentials: "omit" });
  if (!response.ok) throw new PictureError("The picture didn’t upload. Try again.");
}

const pause = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * The whole of sending one picture: a form from the api, the file prepared
 * and posted to the store, then confirmed — which the api answers once the
 * picture is ready, or a moment later if the minute's sweep had it first.
 */
export async function uploadPicture({
  start,
  confirm,
  file,
  crop = CENTRED,
  prepare = preparePicture,
  send = sendToStore,
  wait = pause,
}: {
  start: () => Promise<UploadForm>;
  confirm: (mediaId: string) => Promise<Picture>;
  file: Blob;
  crop?: Crop;
  prepare?: typeof preparePicture;
  send?: typeof sendToStore;
  wait?: (ms: number) => Promise<unknown>;
}): Promise<Picture> {
  const form = await start();
  const prepared = await prepare(file, form, crop);
  await send(form, prepared);
  for (let attempt = 0; ; attempt++) {
    try {
      const picture = await confirm(form.mediaId);
      if (picture.status !== "pending" || attempt >= 4) return picture;
    } catch (error) {
      if (error instanceof ApiError && error.status === 422) {
        throw new PictureError("That picture couldn’t be used. Choose another.");
      }
      // Nothing there yet: the store has it, and says so a moment later.
      if (!(error instanceof ApiError && error.status === 409 && attempt < 4)) throw error;
    }
    await wait(500 * (attempt + 1));
  }
}

/** Whether a file is worth trying at all: a picture, by its type. */
export function looksLikeAPicture(file: unknown): file is File {
  return typeof File !== "undefined" && file instanceof File && file.size > 0 && /^image\//.test(file.type);
}

/**
 * How long a workout photo has left, as a person reads it: "18 h left",
 * "40 min left" — or null once it is over.
 */
export function timeLeft(expiresAt: string | null, now: Date = new Date()): string | null {
  if (!expiresAt) return null;
  const minutes = Math.floor((new Date(expiresAt).getTime() - now.getTime()) / 60_000);
  if (minutes <= 0) return null;
  if (minutes < 60) return `${minutes} min left`;
  return `${Math.floor(minutes / 60)} h left`;
}
